import { Body, Button, Heading, Icon } from '@assembly-js/design-system';
import { Skeleton } from '@mui/material';
import { t } from 'i18next';
import React from 'react';

import { ProxyPurchaseNotice } from 'src/routes/settings/plans/components/ProxyPurchaseGuard';
import { BillingIntervalToggle } from 'src/routes/settings/plans/components/studio/BillingIntervalToggle';
import { getStudioPlanTitle } from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import {
  formatDollars,
  formatStudioPlanDate,
  type SellableStudioPlanId,
  type StudioBillingInterval,
} from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import { readIsProxySession } from 'src/utils/proxySession';

import {
  type AppLimitSurface,
  type CombinedAppLimitQuote,
  toOrdinal,
} from './appLimit';
import { type AppLimitBackTo, AppLimitModalShell } from './AppLimitModalShell';

interface AppLimitCombinedModalProps {
  onClose: () => void;
  /** Sends the plan and the app into one Stripe checkout. */
  onConfirm: () => void;
  plan: SellableStudioPlanId;
  /** Undefined while the catalog loads; the prices render as skeletons. */
  quote: CombinedAppLimitQuote | undefined;
  interval: StudioBillingInterval;
  onChangeInterval: (interval: StudioBillingInterval) => void;
  /** Free's allowance, quoted as the limit the workspace has reached. */
  includedApps: number;
  /** The live count, so the title names the app this action would add. */
  countableApps: number;
  /** Locked open while the checkout is being created, as the upgrade modal is. */
  isSubmitting?: boolean;
  /**
   * The plan applied directly but the app's charge was refused. Upgrading again
   * is a no-op that retries the charge, so the CTA stays as the retry.
   */
  hasChargeFailed?: boolean;
  surface: AppLimitSurface;
  backTo?: AppLimitBackTo;
}

const getTitle = (surface: AppLimitSurface, position: string): string =>
  surface === 'publish'
    ? t(($) => $.page_apps.app_builder.app_limit_modal.combined.title.publish, {
        position,
      })
    : t(($) => $.page_apps.app_builder.app_limit_modal.combined.title.add, {
        position,
      });

// The renewal lands one period after today, since checkout starts a new
// subscription rather than joining the Free one's cycle.
const getRenewalDate = (interval: StudioBillingInterval): string => {
  const date = new Date();
  if (interval === 'year') {
    date.setFullYear(date.getFullYear() + 1);
  } else {
    date.setMonth(date.getMonth() + 1);
  }
  return formatStudioPlanDate(date.toISOString());
};

const PriceRow: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between gap-4">
    <Body tag="span" className="leading-[22px] text-black-heading">
      {label}
    </Body>
    {value === undefined ? (
      <Skeleton variant="text" width={72} />
    ) : (
      <Body tag="span" className="leading-[22px] text-black-heading">
        {value}
      </Body>
    )}
  </div>
);

/**
 * The paywall for a Free workspace whose allowance already equals the paid
 * one. A plan alone would not make room, so the plan and the extra app are sold
 * as one purchase and the user is asked once, with the whole charge itemised.
 */
export const AppLimitCombinedModal: React.FC<AppLimitCombinedModalProps> = ({
  onClose,
  onConfirm,
  plan,
  quote,
  interval,
  onChangeInterval,
  includedApps,
  countableApps,
  isSubmitting = false,
  hasChargeFailed = false,
  surface,
  backTo,
}) => {
  const planTitle = getStudioPlanTitle(plan);
  const title = getTitle(surface, toOrdinal(countableApps + 1));
  const extraApps = quote?.extraApps ?? 1;
  const perMonth = (cents: number | undefined) =>
    cents === undefined
      ? undefined
      : t(($) => $.page_apps.app_builder.app_limit_modal.combined.per_month, {
          amount: formatDollars(cents),
        });

  return (
    <AppLimitModalShell
      ariaLabel={title}
      isDismissDisabled={isSubmitting}
      backTo={backTo}
      onClose={onClose}
    >
      <div
        className={`flex flex-col gap-4 bg-white px-4 pb-8 sm:px-6 sm:pb-5 ${
          backTo ? 'pt-3' : 'pt-6'
        }`}
      >
        <div className="flex flex-col gap-1">
          <Heading
            size="xl"
            tag="h2"
            className="mb-0! pr-8 leading-[28px] text-black-heading"
          >
            {title}
          </Heading>
          <Body tag="div" className="leading-[22px] text-gray-small">
            {t(
              ($) =>
                $.page_apps.app_builder.app_limit_modal.combined.description,
              {
                count: includedApps,
                plan: planTitle,
                fee: quote ? formatDollars(quote.appMonthlyCents) : '',
              },
            )}
          </Body>
        </div>

        <BillingIntervalToggle
          interval={interval}
          onChangeInterval={onChangeInterval}
        />

        <div className="flex flex-col gap-3 rounded border border-solid border-gray-150 p-5">
          <Body tag="div" className="font-medium text-black-heading">
            {t(
              ($) => $.page_apps.app_builder.app_limit_modal.combined.summary,
              {
                plan: planTitle,
                count: extraApps,
              },
            )}
          </Body>
          <div className="flex items-baseline gap-2">
            {quote ? (
              <Heading
                size="xl"
                tag="span"
                className="mb-0! text-black-heading"
              >
                {formatDollars(quote.dueTodayCents)}
              </Heading>
            ) : (
              <Skeleton variant="text" width={96} height={32} />
            )}
            <Body tag="span" size="sm" className="text-gray-small">
              {t(
                ($) =>
                  $.page_apps.app_builder.app_limit_modal.combined.due_today,
              )}
            </Body>
          </div>
        </div>

        <div className="flex flex-col gap-2 rounded bg-gray-100 p-5">
          <Body tag="div" className="mb-1 font-medium text-black-heading">
            {t(
              ($) =>
                $.page_apps.app_builder.app_limit_modal.combined
                  .breakdown_title,
            )}
          </Body>
          <PriceRow
            label={t(
              ($) => $.page_apps.app_builder.app_limit_modal.combined.plan_line,
              { plan: planTitle },
            )}
            value={perMonth(quote?.planMonthlyCents)}
          />
          <PriceRow
            label={t(
              ($) => $.page_apps.app_builder.app_limit_modal.combined.app_line,
              { count: extraApps },
            )}
            value={perMonth(
              quote ? quote.appMonthlyCents * extraApps : undefined,
            )}
          />
          {interval === 'year' && (
            <PriceRow
              label={t(
                ($) =>
                  $.page_apps.app_builder.app_limit_modal.combined
                    .annual_billing,
              )}
              value={t(
                ($) =>
                  $.page_apps.app_builder.app_limit_modal.combined
                    .twelve_months,
              )}
            />
          )}
          <PriceRow
            label={t(
              ($) =>
                $.page_apps.app_builder.app_limit_modal.combined.due_today_line,
            )}
            value={quote ? formatDollars(quote.dueTodayCents) : undefined}
          />

          <Body tag="div" className="mt-3 font-medium text-black-heading">
            {t(
              ($) =>
                $.page_apps.app_builder.app_limit_modal.combined.next_cycle,
              { date: getRenewalDate(interval) },
            )}
          </Body>
          <div className="flex gap-2">
            <Icon icon="Check" className="mt-1 size-3 shrink-0" />
            <div className="flex flex-col">
              <Body tag="span" className="leading-[22px] text-black-heading">
                {quote
                  ? interval === 'year'
                    ? t(
                        ($) =>
                          $.page_apps.app_builder.app_limit_modal.combined
                            .renews_at.year,
                        { amount: formatDollars(quote.renewalCents) },
                      )
                    : t(
                        ($) =>
                          $.page_apps.app_builder.app_limit_modal.combined
                            .renews_at.month,
                        { amount: formatDollars(quote.renewalCents) },
                      )
                  : null}
              </Body>
              <Body tag="span" size="sm" className="text-gray-small">
                {t(
                  ($) =>
                    $.page_apps.app_builder.app_limit_modal.combined
                      .renews_includes,
                  { plan: planTitle, count: extraApps },
                )}
              </Body>
            </div>
          </div>
        </div>

        <Body tag="div" size="sm" className="text-gray-small">
          {t(($) => $.page_apps.app_builder.app_limit_modal.combined.footer)}
        </Body>

        {hasChargeFailed && (
          <Body tag="div" size="sm" className="text-red-600">
            {t(($) => $.page_apps.app_builder.app_limit_modal.charge_failed)}
          </Body>
        )}
        <ProxyPurchaseNotice />
        <div className="flex gap-3">
          <Button
            variant="secondary"
            size="sm"
            label={t(
              ($) => $.page_apps.app_builder.app_limit_modal.combined.cancel,
            )}
            className="box-border flex-1"
            disabled={isSubmitting}
            onClick={onClose}
          />
          <Button
            variant="primary"
            size="sm"
            label={t(
              ($) => $.page_apps.app_builder.app_limit_modal.combined.cta,
            )}
            className="box-border flex-1"
            loading={isSubmitting}
            disabled={!quote || isSubmitting || readIsProxySession()}
            onClick={onConfirm}
          />
        </div>
      </div>
    </AppLimitModalShell>
  );
};
